import i18n from "i18next";

export const aiAssistantConnectionTypes = Object.freeze({
  OAUTH: "oauth",
  TOKEN: "token"
});

export const aiAssistantSetupCopyTypes = Object.freeze({
  INSTRUCTIONS: "instructions",
  CONFIG_LINK: "configLink",
  TOKEN_STEPS: "tokenSteps"
});

export const aiAssistantHelpArticles = Object.freeze({
  PART_1: "part1",
  PART_2: "part2"
});

export const aiAssistants = [
  {
    id: "chatgpt",
    name: "ChatGPT",
    connectionType: aiAssistantConnectionTypes.OAUTH,
    setupCopyType: aiAssistantSetupCopyTypes.INSTRUCTIONS,
    instructionsKey: "aiAssistants.chatgptInstructions",
    isTop: true
  },
  {
    id: "claude",
    name: "Claude",
    oauthClientIdMatch: "claude.ai",
    connectionType: aiAssistantConnectionTypes.OAUTH,
    setupCopyType: aiAssistantSetupCopyTypes.CONFIG_LINK,
    configArticle: aiAssistantHelpArticles.PART_1,
    configAnchor: "Claude-9smWv",
    isTop: true
  },
  {
    id: "grok",
    name: "Grok",
    connectionType: aiAssistantConnectionTypes.TOKEN,
    setupCopyType: aiAssistantSetupCopyTypes.CONFIG_LINK,
    configArticle: aiAssistantHelpArticles.PART_1,
    configAnchor: "Grok-Q0SEV",
    isTop: true
  },
  {
    id: "grokBot",
    name: "Grok Bot",
    connectionType: aiAssistantConnectionTypes.TOKEN,
    setupCopyType: aiAssistantSetupCopyTypes.CONFIG_LINK,
    configArticle: aiAssistantHelpArticles.PART_1,
    configAnchor: "Grok-Bot-YEUaG",
    isTop: true
  },
  {
    id: "museAi",
    name: "Muse",
    connectionType: aiAssistantConnectionTypes.TOKEN,
    setupCopyType: aiAssistantSetupCopyTypes.TOKEN_STEPS,
    configArticle: aiAssistantHelpArticles.PART_1,
    configAnchor: "Muse-AI-OUTMw",
    isTop: true
  },
  {
    id: "gemini",
    name: "Gemini",
    oauthClientIdMatch: "kubera-gemini",
    connectionType: aiAssistantConnectionTypes.OAUTH,
    setupCopyType: aiAssistantSetupCopyTypes.CONFIG_LINK,
    configArticle: aiAssistantHelpArticles.PART_1,
    configAnchor: "Gemini-yGXy1",
    isTop: true
  },
  {
    id: "perplexity",
    name: "Perplexity",
    connectionType: aiAssistantConnectionTypes.TOKEN,
    setupCopyType: aiAssistantSetupCopyTypes.TOKEN_STEPS,
    configArticle: aiAssistantHelpArticles.PART_1,
    configAnchor: "Perplexity-7MPgX",
    isTop: true
  },
  {
    id: "antigravityCli",
    name: "Antigravity CLI",
    connectionType: aiAssistantConnectionTypes.TOKEN,
    setupCopyType: aiAssistantSetupCopyTypes.TOKEN_STEPS,
    configArticle: aiAssistantHelpArticles.PART_2,
    configAnchor: "Gemini-CLI-sirFp",
    configLinkKey: "aiAssistants.configurationsInstructions"
  },
  {
    id: "chatgptApp",
    name: "ChatGPT App",
    connectionType: aiAssistantConnectionTypes.TOKEN,
    isDeprecated: true
  },
  {
    id: "cursor",
    name: "Cursor",
    connectionType: aiAssistantConnectionTypes.TOKEN,
    setupCopyType: aiAssistantSetupCopyTypes.CONFIG_LINK,
    configArticle: aiAssistantHelpArticles.PART_2,
    configAnchor: "Cursor-Desktop-yYEeR"
  }
];

export const genericAIAssistant = {
  id: "others",
  name: i18n.t("aiAssistants.others"),
  connectionType: aiAssistantConnectionTypes.TOKEN,
  setupCopyType: aiAssistantSetupCopyTypes.TOKEN_STEPS,
  configArticle: aiAssistantHelpArticles.PART_2,
  configAnchor: "GenericOthers-0GK1_",
  isGeneric: true
};

export const topAIAssistants = aiAssistants.filter(assistant => assistant.isTop === true);

export const otherAIAssistants = aiAssistants.filter(assistant => assistant.isTop !== true);

const normalize = value => (value || "").trim().toLowerCase();

const nameMatchesAssistant = (assistant, name) => {
  const assistantName = normalize(assistant.name);
  const candidate = normalize(name);
  return candidate === assistantName || candidate.startsWith(`${assistantName} `);
};

export const findAIAssistantByName = name =>
  aiAssistants
    .filter(assistant => nameMatchesAssistant(assistant, name))
    .sort((a, b) => b.name.length - a.name.length)[0];

const isSameAssistant = (assistant, name) => findAIAssistantByName(name)?.id === assistant.id;

export const findAIAssistantToken = (tokens, assistant) => tokens.find(token => isSameAssistant(assistant, token.name));

const findAIAssistantByClientId = clientId => {
  const candidate = normalize(clientId);
  return candidate === ""
    ? undefined
    : aiAssistants
        .filter(
          assistant => !assistant.oauthClientIdMatch === false && candidate.includes(assistant.oauthClientIdMatch)
        )
        .sort((a, b) => b.oauthClientIdMatch.length - a.oauthClientIdMatch.length)[0];
};

export const findAIAssistantForClient = clientInfo =>
  findAIAssistantByClientId(clientInfo?.id) || findAIAssistantByName(clientInfo?.name);

export const findAIAssistantOAuthConnections = (connections, assistant) =>
  connections.filter(connection => findAIAssistantForClient(connection.clientInfo)?.id === assistant.id);

export const isAIAssistantConnected = (assistant, tokens, connections) =>
  !findAIAssistantToken(tokens, assistant) === false ||
  findAIAssistantOAuthConnections(connections, assistant).length > 0;

export const getNextGenericAIAssistantName = tokens => {
  const nameTemplate = i18n.t("aiAssistants.genericModelName");
  const usedNames = new Set(tokens.map(token => normalize(token.name)));
  let index = 1;

  while (usedNames.has(normalize(nameTemplate.replace("%n%", index)))) {
    index += 1;
  }
  return nameTemplate.replace("%n%", index);
};

export const getUnknownAIAssistantTokens = tokens =>
  tokens.filter(token => !findAIAssistantByName(token.name) === true);

export const getUnknownAIAssistantOAuthConnections = connections =>
  connections.filter(connection => !findAIAssistantForClient(connection.clientInfo) === true);
